import React, { useContext, useEffect, useState } from 'react';
import DialogBox from '../../../Common/Layout/DialogBox';
import {
  IAllocationSector,
  IAllocationSectorInput,
  ISector,
  useCreateAllocationSectorMutation,
  useSectorsQuery,
  useUpdateAllocationSectorMutation
} from '../../../../generated/graphql';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { Colors } from '../../../../utils/theme';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import InputComponent from '../../../Common/Fields/InputComponent';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';

interface IProps {
  stockId: string;
  companyName: string;
  handleClose: (saved: boolean, isNew?: boolean, allSectorInput?: IAllocationSector) => void;
  allSectorInput: IAllocationSector | null;
}

type IErrors = {
  sectorId: string | null;
  value: string | null;
  year: string | null;
};

const defaultErrorsObj: IErrors = {
  sectorId: null,
  value: null,
  year: null
};

const errorTexts = {
  sectorId: 'Sector is required!',
  value: 'Sector % is required!',
  year: 'Year is required!'
};

const AllocationSectorForm: React.FunctionComponent<IProps> = ({
  stockId,
  companyName,
  handleClose,
  allSectorInput
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editable, setEditable] = useState(allSectorInput === null);
  const [sectorId, setSectorId] = useState(allSectorInput?.sector_id || null);
  const [sectorTypeOption, setSectorTypeOption] = useState<ISector | null>(null);
  const [sectorValue, setSectorValue] = useState(allSectorInput?.value || null);
  const [sectorYear, setSectorYear] = useState(allSectorInput?.year || null);
  const allSectors = useSectorsQuery();
  const [createAllSector, createAllSectorResult] = useCreateAllocationSectorMutation();
  const [updateAllSector, updateAllSectorResult] = useUpdateAllocationSectorMutation();
  const canEdit = canUserEdit(state.user, Types.Stock);

  useEffect(() => {
    if (allSectors.data && allSectors.data.result) {
      if (sectorId) {
        setSectorTypeOption(allSectors.data.result.find((s) => s?.id === sectorId) || null);
      }
    }
  }, [allSectors]);

  const handleSectorChange = (_: unknown, newValue: ISector | null) => {
    setFormErrors({ ...formErrors, sectorId: newValue ? null : errorTexts.sectorId });
    setSectorTypeOption(newValue);
    setSectorId(newValue ? newValue.id : null);
  };

  const handleSectorValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({ ...formErrors, value: value ? null : errorTexts.value });
    setSectorValue(value);
  };

  const handleSectorYearChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({ ...formErrors, year: value ? null : errorTexts.year });
    setSectorYear(value);
  };

  const handleCancel = () => {
    if (editable && allSectorInput !== null) {
      setSectorId(allSectorInput?.sector_id || null);
      if (allSectors.data && allSectors.data.result) {
        if (allSectorInput?.sector_id) {
          setSectorTypeOption(
            allSectors.data.result.find((s) => s?.id === allSectorInput?.sector_id) || null
          );
        }
      }
      setSectorValue(allSectorInput?.value || null);
      setSectorYear(allSectorInput?.year || null);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      sectorId: null,
      value: null,
      year: null
    };

    if (!sectorId) {
      hasErrors = true;
      errors.sectorId = errorTexts.sectorId;
    }
    if (!sectorValue) {
      hasErrors = true;
      errors.value = errorTexts.value;
    }
    if (!sectorYear) {
      hasErrors = true;
      errors.year = errorTexts.year;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      if (allSectorInput) {
        updateAllSector({
          variables: {
            id: allSectorInput.id,
            input: getAllSectorInput() as IAllocationSectorInput
          },
          update(_, { data }) {
            handleClose(true, false, data?.result as IAllocationSector);
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: 'Allocation Sector Updated'
              }
            });
          }
        }).catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message
            }
          });
        });
      } else {
        createAllSector({
          variables: {
            stock_id: stockId,
            input: getAllSectorInput() as IAllocationSectorInput
          },
          update(_, { data }) {
            handleClose(true, true, data?.result as IAllocationSector);
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: 'Allocation Sector Created'
              }
            });
          }
        }).catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message
            }
          });
        });
      }
    }
  };

  const getAllSectorInput = () => ({
    sector_id: sectorId,
    year: sectorYear,
    value: sectorValue
  });

  return (
    <DialogBox
      id="allocation-sector-dialog"
      width={690}
      title={allSectorInput ? 'Allocation Sector Details' : 'Create Allocation Sector'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {allSectorInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="allocation-sector-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          mt: '20px',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '24px'
        }}
      >
        <InputComponent
          id="allocation-sector-form-company"
          label="Company"
          value={companyName}
          disabled={true}
          additionalStyle={{ gridColumn: 'span 4' }}
        />
        <AutocompleteComponent
          id="allocation-sector-autocomplete"
          data-testid="allocation-sector-autocomplete"
          label="Sector Type"
          options={(allSectors.data?.result as never[]) || []}
          getOptionLabel={(option: ISector) => option?.name}
          values={sectorTypeOption}
          onChange={handleSectorChange}
          disabled={createAllSectorResult.loading || updateAllSectorResult.loading || !editable}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <InputComponent
          id={`allocation-sector-form-sector-value`}
          data-testid={`allocation-sector-form-sector-value`}
          label={'Sector %'}
          type="number"
          value={sectorValue || ''}
          disabled={createAllSectorResult.loading || updateAllSectorResult.loading || !editable}
          onChange={(e) => handleSectorValueChange(e)}
          errorMsg={formErrors.value}
        />
        <InputComponent
          id={`allocation-sector-form-sector-year`}
          data-testid={`allocation-sector-form-sector-year`}
          label={'Date Year'}
          type="number"
          value={sectorYear || ''}
          disabled={createAllSectorResult.loading || updateAllSectorResult.loading || !editable}
          onChange={(e) => handleSectorYearChange(e)}
          errorMsg={formErrors.year}
        />
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-allocation-sector-button"
          onClick={handleCancel}
          disabled={createAllSectorResult.loading || updateAllSectorResult.loading}
          text={allSectorInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-allocation-sector-button"
          onClick={handleSubmit}
          disabled={createAllSectorResult.loading || updateAllSectorResult.loading || !editable}
          text={allSectorInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default AllocationSectorForm;
